import {
  type LinearSubstep,
  SimpleCsticId,
  type SubstepTile,
  type SubstepTileExtended,
} from "~/features/configurator/Linear/models";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import { ConfiguratorType, type ShaftConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { useConfiguratorType } from "~/features/configurator/common/useConfiguratorType";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { TileContainer } from "~/features/configurator/common/components/GenericTileSubstep/GenericTileSubstep";
import LinearTile, {
  LCTileImage,
  type LinearTileProps,
  StandardStarComponent,
} from "~/features/configurator/Linear/components/LinearTile";
import { useDispatch, useSelector } from "react-redux";
import { formatCsticsToSet } from "~/features/configurator/Linear/utils";
import { type CustomDispatch } from "~/app/store/types";
import { type RootState } from "~/app/store";
import { getKmats } from "~/features/configurator/Linear/redux/selectors";
import { isTileEnabled, isTileStandard } from "~/features/configurator/common/tileUtils";
import { updateCsticValuesFromTile } from "~/features/configurator/Linear/redux/actions";
import classes from "~/features/configurator/Shaft/substeps/ShaftToleranceSubstep.module.less";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { Card } from "~/features/configurator/Linear/components/Card";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import classNames from "classnames";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { selectCstic } from "~/features/configurator/Linear/redux/selectCstic";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import * as MetricCstics from "~/features/configurator/Shaft/metric/metricCsticConstants";
import * as InchCstics from "~/features/configurator/Shaft/inch/inchCsticConstants";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { SimpleCsticNumberField } from "~/features/configurator/Shaft/components/SimpleCsticNumberField";
import { formatShaftToleranceMinmax } from "~/features/configurator/Shaft/shaftConfiguratorHelper";
import { INCH_UNIT, METRIC_UNIT } from "~/features/configurator/Shaft/common/commonShaftConstants";
import { KmatType } from "~/features/configurator/Linear/kmatType";

interface Props {
  // Actually substepIndex
  csticIndex: number;
  // Actually substepAmount
  csticAmount: number;
  def: LinearSubstep;
}

const CONFIGURATION = {
  [ConfiguratorType.MetricShaft]: {
    kmatType: KmatType.Main,
    cstic: SimpleCsticId(MetricCstics.shaftTolerance.value),
    values: ["LN", "LS", "LNS"],
    minmax: {
      ln: SimpleCsticId(MetricCstics.shaftTolerance.minmax.ln),
      ls: SimpleCsticId(MetricCstics.shaftTolerance.minmax.ls),
    },
    maxDecimal: MetricCstics.shaftTolerance.maxDecimal,
    unit: METRIC_UNIT,
    specialTile: {
      value: "LNS",
      minmax: "VLMAX_01T_060",
      upper: "VLMAX_01_02_060",
      lower: "VLMAX_01_01_060",
    },
  },
  [ConfiguratorType.InchShaft]: {
    kmatType: KmatType.Main,
    cstic: SimpleCsticId(InchCstics.shaftTolerance.value),
    values: ["LN", "LS"],
    minmax: {
      ln: SimpleCsticId(InchCstics.shaftTolerance.minmax.ln),
      ls: SimpleCsticId(InchCstics.shaftTolerance.minmax.ls),
    },
    maxDecimal: InchCstics.shaftTolerance.maxDecimal,
    unit: INCH_UNIT,
  },
};

const generateTiles = (
  def: LinearSubstep,
  dispatch: CustomDispatch<RootState>,
  config: (typeof CONFIGURATION)[keyof typeof CONFIGURATION],
): (SubstepTileExtended & { value: string })[] => {
  const tiles: (SubstepTile & { value: string })[] = config.values.map((value, index) => ({
    cstics: { [config.cstic.key]: value },
    value,
    name: `configurator.shaft.step.2.substep.2.tile.${index + 1}.title`,
    description: "",
    isDisabledExternally: false,
    info: "",
    imageUrl: "",
    csticsToEnable: {},
  }));
  return tiles.map((tile, index) => ({
    ...tile,
    onClick: () => dispatch(updateCsticValuesFromTile(def.type, 0, tile)),
    tileIndex: index,
  }));
};

export const ShaftToleranceSubstep = ({ def, ...props }: Props) => {
  const configuratorType = useConfiguratorType() as ShaftConfiguratorType;
  const config = CONFIGURATION[configuratorType];
  const debugLanguage = isDebugLanguageEnabled();

  const dispatch: CustomDispatch<RootState> = useDispatch();
  const kmats = useSelector(getKmats);

  const tiles = generateTiles(def, dispatch, config);

  const curValue = useSelector(selectCstic(config.cstic))?.value;

  let minmaxLN = useSelector(selectCstic(config.minmax.ln))?.value;
  minmaxLN = formatShaftToleranceMinmax(minmaxLN, config.unit, config.maxDecimal);
  let minmaxLS = useSelector(selectCstic(config.minmax.ls))?.value;
  minmaxLS = formatShaftToleranceMinmax(minmaxLS, config.unit, config.maxDecimal);

  const minmaxes = {
    LN: minmaxLN,
    LS: minmaxLS,
  };

  return (
    <SubstepFrame
      title={def.name}
      description=""
      info={def.info}
      isPicked={def.selectedTile !== null}
      errorMessage={""}
      {...props}
    >
      <TileContainer columnAmount={4} className={classes.tileContainer}>
        {tiles.slice(0, 2).map((data, index) => (
          <LinearTile
            key={index}
            onClickHandler={data.onClick}
            imageUrl={data.imageUrl}
            title={debugLanguage ? formatCsticsToSet(data.cstics) : data.name}
            description={minmaxes[data.value as keyof typeof minmaxes]}
            info={data.info}
            isPicked={curValue === config.values[index]}
            isDisabled={!isTileEnabled(data, kmats)}
            isStandard={isTileStandard(data, kmats)}
          />
        ))}
        {/* Disabled until further notice */}
        {/*{tiles[2] && (*/}
        {/*  <WideTile*/}
        {/*    index={2}*/}
        {/*    className={classes.wideTile}*/}
        {/*    onClickHandler={tiles[2].onClick}*/}
        {/*    imageUrl={tiles[2].imageUrl}*/}
        {/*    title={debugLanguage ? formatCsticsToSet(tiles[2].cstics) : tiles[2].name}*/}
        {/*    description={tiles[2].description}*/}
        {/*    info={tiles[2].info}*/}
        {/*    isPicked={tiles[2].tileIndex === def.selectedTile}*/}
        {/*    isDisabled={!isTileEnabled(tiles[2], kmats)}*/}
        {/*    isStandard={isTileStandard(tiles[2], kmats)}*/}
        {/*  />*/}
        {/*)}*/}
      </TileContainer>
    </SubstepFrame>
  );
};

/*
Copy from Linear Tile - Make Linear Tile a composable component...
 */
const WideTile = (props: LinearTileProps) => {
  const {
    className,
    description,
    title,
    isDisabled,
    isPicked,
    imageUrl,
    info,
    onClickHandler,
    link,
    openLinkInNewTab,
    isStandard,
    forceStandardSpace,
    children,
    selectable = true,
  } = props;

  const { t } = useXmlConfiguratorTranslations();

  const titleElement =
    typeof title === "string" ? (
      <LanguageAwareExternalContent content={title} className={"scha__lc__external-label"} />
    ) : (
      title
    );

  const config = CONFIGURATION[ConfiguratorType.MetricShaft];

  const minmax = useSelector(selectCstic({ key: config.specialTile.minmax, index: 0, type: config.kmatType }))?.value;

  return (
    <Card
      className={classNames("scha__lc__tile", className)}
      onClick={onClickHandler}
      disabled={isDisabled}
      active={isPicked}
      disableHover={isDisabled}
      as={link ? "a" : "div"}
      href={link}
      target={link && openLinkInNewTab ? "_blank" : undefined}
    >
      {imageUrl && <LCTileImage imageUrl={imageUrl} />}

      {selectable && <CheckedCircle className={"scha__lc__tile__checkmark"} checked={isPicked} />}

      <div className={classNames("scha__lc__card__contents", classes.wideTileContent)}>
        <div>
          <p className="scha__lc__card__headline">
            {titleElement}
            <LinearInfo info={info} popupClassName={"scha__lc__external-content--shaft"} />
          </p>

          {description && (
            <p className="scha__lc__card__subheadline">
              <LanguageAwareExternalContent
                content={minmax ? `⁺∕₋ ${minmax}` : undefined}
                className={"scha__lc__external-label"}
              />
            </p>
          )}

          {(isStandard || forceStandardSpace) && (
            <div className={!isStandard ? "v-hidden" : "mt-auto"}>
              <StandardStarComponent />
            </div>
          )}

          {children}
        </div>

        <div onClick={isPicked ? STOP_PROPAGATION : undefined}>
          <SimpleCsticNumberField
            csticId={SimpleCsticId(config.specialTile.upper)}
            substepType={SubstepType.ShaftTolerance}
            label={t!("configurator.shaft.step.2.substep.2.tile.3.lower")}
            disabled={!isPicked}
          />
          <SimpleCsticNumberField
            csticId={SimpleCsticId(config.specialTile.lower)}
            substepType={SubstepType.ShaftTolerance}
            label={t!("configurator.shaft.step.2.substep.2.tile.3.lower")}
            disabled={!isPicked}
          />
        </div>
      </div>
    </Card>
  );
};

const STOP_PROPAGATION = (e: React.MouseEvent) => {
  e.stopPropagation();
};
